/* Первые шаги клиента (H0), слайдшоу «Иконка на экране» и достижение «Курс молодого бойца».
   Разметка и классы — прототипа (screens.js: clFirstSteps, clMock, clSlides, clHowto, clAchShow, clAchShelf).
   Шаги отмечает сервер по факту (clientApp.me.steps); здесь — только где открыто приложение. */
import { useEffect, useState, useSyncExternalStore, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { plural3, QUOTES, streakNext, STREAK_MS } from '@tg/domain';
import { shortDate } from '../lib/dates';
import { useMe } from '../lib/data';
import { I, iHtml } from './icons';
import { toast, useLeave } from './overlay';
import { dequeue, useAchQueue } from './achQueue';

/* ── где открыто ── */
type W = Window & { Capacitor?: { isNativePlatform?: () => boolean }; TelegramWebviewProxy?: unknown };
/** Приложение из стора (Capacitor): иконку ставит магазин, шага с иконкой нет */
export const isNative = () => !!(window as W).Capacitor?.isNativePlatform?.();
/** Запуск с иконки на экране телефона */
export const isStandalone = () => matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
/** Встроенный браузер Telegram: оттуда сайт на экран не добавить. Android и новые iOS дают TelegramWebviewProxy */
export const inTelegram = () => !!(window as W).TelegramWebviewProxy || /Telegram/i.test(navigator.userAgent);
/** Android — Chrome, остальное (iPhone, iPad) — Safari */
export const osOf = (): 'android' | 'ios' => (/Android/i.test(navigator.userAgent) ? 'android' : 'ios');

/** Открыть адрес в обычном браузере из Telegram: Android — intent в Chrome, iOS 17+ — x-safari-https */
export function openInBrowser(url: string) {
  const u = new URL(url);
  location.href =
    osOf() === 'android'
      ? `intent://${u.host}${u.pathname}${u.search}${u.hash}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url)};end`
      : 'x-safari-' + url;
}
export async function copyText(t: string) {
  try {
    await navigator.clipboard.writeText(t);
    return true;
  } catch {
    /* старый WebView — через скрытое поле */
    const a = document.createElement('textarea');
    a.value = t;
    a.style.cssText = 'position:fixed;opacity:0';
    document.body.appendChild(a);
    a.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      /* не вышло */
    }
    a.remove();
    return ok;
  }
}

/* Иконка приложения — знак из брендбука (mvp/brand/logo/trenergram-mark.svg) */
export const markSvg = (cls: string) =>
  `<svg class="${cls}" viewBox="0 0 100 100" aria-hidden="true"><rect width="100" height="100" rx="22.5" fill="#FC5200"/><path fill="#fff" d="M12 32H46V42H34V66L28 72H24V42H12Z"/><path fill="#fff" d="M52 32H62L71 50L80 32H90V72H81V49L73 64H69L61 49V72H52Z"/></svg>`;
export const Mark = ({ cls }: { cls: string }) => <span style={{ display: 'contents' }} dangerouslySetInnerHTML={{ __html: markSvg(cls) }} />;

/* Схемы экранов для слайдшоу: не скриншоты, а упрощённый телефон —
   страница скелетоном, системные кнопки, нужная подсвечена (.hl) */
function mockHtml(k: string) {
  const pg = '<div class="mpg"><i></i><i></i><i></i><b></b></div>';
  const host = location.host || 'trener.my';
  const menu = (rows: [string, string, number?][], cls: string) =>
    `<div class="mmenu ${cls}">${rows.map(([t, ic, hl]) => `<div class="${hl ? 'hl' : ''}"><span>${t}</span>${ic ? iHtml(ic) : ''}</div>`).join('')}</div>`;
  const dots3 = '<svg viewBox="0 0 24 24" class="i"><circle cx="6" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="18" cy="12" r="1.6"/></svg>';
  const dotsV = '<svg viewBox="0 0 24 24" class="i"><circle cx="12" cy="6" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="12" cy="18" r="1.6"/></svg>';
  const appic = markSvg('mapp');
  switch (k) {
    /* iOS 26 Safari: снизу «назад», адрес, «⋯»; «Поделиться» — в меню «⋯» */
    case 'ios-menu':
      return (
        pg +
        menu(
          [
            ['Поделиться', 'share', 1],
            ['Добавить в закладки', 'book'],
            ['Найти на странице', 'search'],
          ],
          'br',
        ) +
        `<div class="msaf"><span class="rb">${iHtml('left')}</span><span class="pill">${host}</span><span class="rb hl">${dots3}</span></div>`
      );
    case 'ios-share':
      return (
        pg +
        `<div class="mdim"></div><div class="msh"><div class="mhd">${appic}<span><b>Тренерграм</b><s>${host}</s></span></div>
      <div class="mapps"><i></i><i></i><i></i><i></i><i></i></div>
      <div class="mlist"><div><span>Скопировать</span>${iHtml('link')}</div><div><span>Добавить в Избранное</span>${iHtml('book')}</div><div class="hl"><span>На экран «Домой»</span>${iHtml('addsq')}</div></div></div>`
      );
    case 'ios-add':
      return `<div class="mnav"><span>Отменить</span><b>На экран «Домой»</b><span class="hl">Добавить</span></div>
      <div class="mcard">${appic}<span><b>Тренерграм</b><s>${host}</s></span></div>
      <div class="mcard row"><span>Открывать как веб-приложение</span><i class="tgl on"></i></div>`;
    case 'home':
      return `<div class="mhome">${Array.from({ length: 12 }, (_, i) => (i === 6 ? `<span class="me hl">${appic}<s>Тренерграм</s></span>` : '<span><i></i><s></s></span>')).join('')}</div>`;
    /* Android Chrome: адрес и «⋮» сверху */
    case 'and-menu':
      return (
        `<div class="mchr"><span class="pill">${host}</span><span class="rb hl">${dotsV}</span></div>` +
        pg +
        menu(
          [
            ['Новая вкладка', 'plus'],
            ['История', 'cal'],
            ['Добавить на гл. экран', 'addsq', 1],
            ['Настройки', 'gear'],
          ],
          'tr',
        )
      );
    case 'and-install':
      return (
        pg +
        `<div class="mdim"></div><div class="mdlg">${appic}<b>Установить приложение?</b><s>Тренерграм · ${host}</s>
      <div class="mbtns"><span>Отмена</span><span class="hl">Установить</span></div></div>`
      );
    /* Встроенный браузер Telegram: сверху «Закрыть», заголовок, меню */
    case 'tg-ios':
    case 'tg-and': {
      const a = k === 'tg-and';
      return (
        `<div class="mtg"><span>${a ? iHtml('x') : 'Закрыть'}</span><b>Тренерграм<s>${host}</s></b><span class="rb hl">${a ? dotsV : dots3}</span></div>` +
        pg +
        menu(
          [
            [a ? 'Открыть в браузере' : 'Открыть в Safari', 'compass', 1],
            ['Скопировать ссылку', 'link'],
            ['Поделиться', 'share'],
          ],
          'tr tgm',
        )
      );
    }
  }
  return pg;
}
export const Mock = ({ k, cls }: { k: string; cls?: string }) => <div className={'mk' + (cls ? ' ' + cls : '')} dangerouslySetInnerHTML={{ __html: mockHtml(k) }} />;

/** Слайды для текущего места: Telegram → сначала в браузер, дальше шаги браузера */
function slides(): [string, string, string][] {
  const and = osOf() === 'android';
  const out: [string, string, string][] = [];
  if (inTelegram())
    out.push([
      'tg-' + (and ? 'and' : 'ios'),
      and ? 'Сначала откройте в Chrome' : 'Сначала откройте в Safari',
      `Telegram не умеет добавлять сайты на экран. Нажмите ${and ? '«⋮»' : '«⋯»'} вверху справа и «Открыть в ${and ? 'браузере' : 'Safari'}», там войдите по номеру ещё раз.`,
    ]);
  if (and)
    out.push(
      ['and-menu', 'Нажмите «⋮» вверху справа', 'Затем — «Добавить на гл. экран».'],
      ['and-install', 'Нажмите «Установить»', 'Chrome сам положит иконку на экран телефона.'],
      ['home', 'Готово — Тренерграм на экране', 'Открывайте его с иконки, как обычное приложение.'],
    );
  else
    out.push(
      ['ios-menu', 'Нажмите «⋯» внизу справа', 'Затем — «Поделиться». На старых iPhone кнопка «Поделиться» сразу внизу экрана.'],
      ['ios-share', 'Выберите «На экран „Домой“»', 'Если пункта не видно — пролистайте список вниз.'],
      ['ios-add', 'Нажмите «Добавить»', 'Переключатель «Открывать как веб-приложение» оставьте включённым.'],
      ['home', 'Готово — Тренерграм на экране', 'Открывайте его с иконки. В первый раз войдите по номеру ещё раз — это 10 секунд.'],
    );
  return out;
}

/* Слайдшоу открывается из любого места («Как?» в первых шагах, «Установить» в подсказке установки) */
let howOpen = false;
const howSubs = new Set<() => void>();
export function openHowto() {
  howOpen = true;
  howSubs.forEach((f) => f());
}
const closeHowto = () => {
  howOpen = false;
  howSubs.forEach((f) => f());
};

/** Слайдшоу — лист снизу; листается кнопками и свайпом */
export function Howto() {
  const open = useSyncExternalStore(
    (f) => {
      howSubs.add(f);
      return () => howSubs.delete(f);
    },
    () => howOpen,
  );
  const { mounted, out } = useLeave(open);
  const [sl, setSl] = useState(slides);
  const [st, setSt] = useState<{ i: number; dir: '' | 'fwd' | 'bk' }>({ i: 0, dir: '' });
  const [x0, setX0] = useState<number | null>(null);
  useEffect(() => {
    if (open) {
      setSl(slides());
      setSt({ i: 0, dir: '' });
    }
  }, [open]);
  if (!mounted) return null;
  const { i, dir } = st;
  const [m, t, d] = sl[Math.min(i, sl.length - 1)];
  const last = i === sl.length - 1;
  const go = (k: number) => {
    const n = i + k;
    if (n >= 0 && n < sl.length) setSt({ i: n, dir: k > 0 ? 'fwd' : 'bk' });
  };
  /* Перелистывание — лист на месте, едет только схема */
  const still = dir ? { animation: 'none' } : undefined;
  return createPortal(
    <div className={'shw' + (out ? ' out' : '')}>
      <div className="shbg" style={still} onClick={closeHowto} />
      <div className="sheet howto" style={still} role="dialog" aria-label="Иконка на экране">
        <i className="grab" />
        <div className="hth">
          <s>
            Шаг {i + 1} из {sl.length}
          </s>
          <button className="ib sm" onClick={closeHowto} aria-label="Закрыть">
            <I n="x" />
          </button>
        </div>
        <div
          onPointerDown={(e) => setX0(e.clientX)}
          onPointerUp={(e) => {
            if (x0 == null) return;
            const dx = e.clientX - x0;
            setX0(null);
            if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
          }}
        >
          <Mock key={i} k={m} cls={dir} />
        </div>
        <h2>{t}</h2>
        <p>{d}</p>
        <div className="hdots">
          {sl.map((_, k) => (
            <i key={k} className={k === i ? 'on' : ''} />
          ))}
        </div>
        <div className="hbtn">
          {i ? (
            <button className="btn sec" onClick={() => go(-1)}>
              Назад
            </button>
          ) : null}
          <button className="btn pri" onClick={() => (last ? closeHowto() : go(1))}>
            {last ? 'Понятно' : 'Дальше'}
          </button>
        </div>
      </div>
    </div>,
    document.getElementById('dev') || document.body,
  );
}

/* ═══ Достижения «за первое» ═══ (ACH-2)
   Четыре значка, только эмоциональные — ничего не открывают и не дают.
   Значок — гиря на шестиграннике: новобранец в пилотке, гиря в короне,
   гиря с рулеткой, гиря с гантелью. Разметка/арт — 1:1 из прототипа. */
export const ACH_NAME = 'Курс молодого бойца';
const HEX = (id: string) => `<defs><linearGradient id="hx${id}" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#FF7A1F"/><stop offset=".5" stop-color="#FC5200"/><stop offset="1" stop-color="#E24400"/></linearGradient>
  <radialGradient id="kb${id}" cx=".35" cy=".3" r=".8"><stop offset="0" stop-color="#4A403B"/><stop offset="1" stop-color="#1E1A18"/></radialGradient></defs>
  <path d="M60 5 108 32.5v55L60 115 12 87.5v-55z" fill="url(#hx${id})"/>
  <path d="M60 15 99 37.5v45L60 105 21 82.5v-45z" fill="none" stroke="#fff" stroke-opacity=".4" stroke-width="2"/>`;
const HEX_OFF = `<path d="M60 5 108 32.5v55L60 115 12 87.5v-55z" fill="var(--c-fill2)"/>
  <path d="M60 15 99 37.5v45L60 105 21 82.5v-45z" fill="none" stroke="var(--line2)" stroke-width="2"/>`;
const KB = (id: string) => `<path d="M44 58c-6-30 38-30 32 0" fill="none" stroke="#1E1A18" stroke-width="9" stroke-linecap="round"/>
  <path d="M47 54c-3-20 26-22 26-4" fill="none" stroke="#5A4E48" stroke-width="2" stroke-linecap="round" opacity=".7"/>
  <circle cx="60" cy="76" r="27" fill="url(#kb${id})"/>`;
const SWEAT = '<path class="sweat" d="M29 47c3 5 3 8 0 9.5-3-1.5-3-4.5 0-9.5z" fill="#9BDCFF" stroke="#1E1A18" stroke-width="1"/>';
const ARM_L = '<path d="M35 80c-8 1-10 8-6 12" fill="none" stroke="#1E1A18" stroke-width="6" stroke-linecap="round"/>';
const RIBBON = (word: string, dark: string, light: string, ink: string) => `<path d="M26 102l6-4h56l6 4-6 4H32z" fill="${dark}" stroke="#1E1A18" stroke-width="1.4" stroke-linejoin="round"/>
  <path d="M34 97h52v10H34z" fill="${light}" stroke="#1E1A18" stroke-width="1.4"/>
  <text x="60" y="105.2" text-anchor="middle" font-family="Montserrat,Arial Black,sans-serif" font-weight="900" font-size="8.5" letter-spacing="${word.length > 3 ? 1 : 1.5}" fill="${ink}">${word}</text>`;
/* «Курс молодого бойца»: гиря-новобранец — пилотка набекрень, глаза вразбег, отдаёт честь, потеет */
const kmb = (id: string) => `${HEX(id)}
  ${SWEAT}
  ${ARM_L}
  ${KB(id)}
  <g transform="rotate(-12 60 54)"><path d="M40 58c5-11 35-12 41-1l-1.5 4c-11-4-27-4-38 1.5z" fill="#7A8A3E" stroke="#1E1A18" stroke-width="1.6" stroke-linejoin="round"/>
    <path d="M60 53.5l1 2.2 2.4.3-1.8 1.6.5 2.4-2.1-1.2-2.1 1.2.5-2.4-1.8-1.6 2.4-.3z" fill="#FF3B30" stroke="#1E1A18" stroke-width=".5"/></g>
  <circle cx="50" cy="71" r="7.5" fill="#fff" stroke="#1E1A18" stroke-width="1.2"/>
  <circle cx="70" cy="70" r="8.5" fill="#fff" stroke="#1E1A18" stroke-width="1.2"/>
  <g class="pups"><circle cx="53.5" cy="73.5" r="3.3" fill="#111"/><circle cx="66" cy="67.5" r="3.6" fill="#111"/></g>
  <circle cx="43" cy="82" r="3.5" fill="#FF7A8A" opacity=".75"/><circle cx="78" cy="81" r="3.5" fill="#FF7A8A" opacity=".75"/>
  <path d="M48 81c4 13 20 13 24 0z" fill="#7A1F1F" stroke="#1E1A18" stroke-width="1.2" stroke-linejoin="round"/>
  <path d="M50 81.3h20l-1 3H51z" fill="#fff"/><ellipse cx="60" cy="88.5" rx="5" ry="2.6" fill="#FF7A8A"/>
  <g class="arm"><path d="M85 80c10-6 9-17 1-24" fill="none" stroke="#1E1A18" stroke-width="6" stroke-linecap="round"/>
    <path d="M78.5 54l10-3 1.4 4.6-10 3z" fill="#1E1A18" stroke="#1E1A18" stroke-width="2" stroke-linejoin="round"/></g>
  ${RIBBON('КМБ', '#7A8A3E', '#8E9F49', '#fff')}`;
/* «Первая тренировка»: гиря качает гантель на бицепс, косится на неё, язык набок от усердия, пот градом */
const wkb = (id: string) => `${HEX(id)}
  ${SWEAT}
  <path class="sweat" d="M24 66c2.4 4 2.4 6.4 0 7.6-2.4-1.2-2.4-3.6 0-7.6z" fill="#9BDCFF" stroke="#1E1A18" stroke-width="1"/>
  ${ARM_L}
  <g class="arm"><path d="M85 79c9-4 12-12 10-22" fill="none" stroke="#1E1A18" stroke-width="6" stroke-linecap="round"/>
    <path d="M84 53h22" stroke="#1E1A18" stroke-width="3" stroke-linecap="round"/>
    <rect x="82" y="46" width="5" height="14" rx="1.5" fill="#5A4E48" stroke="#1E1A18" stroke-width="1.2"/>
    <rect x="103" y="46" width="5" height="14" rx="1.5" fill="#5A4E48" stroke="#1E1A18" stroke-width="1.2"/>
    <circle cx="95" cy="54" r="5" fill="#1E1A18"/></g>
  ${KB(id)}
  <circle cx="50" cy="70" r="7.5" fill="#fff" stroke="#1E1A18" stroke-width="1.2"/>
  <circle cx="70" cy="70" r="7.5" fill="#fff" stroke="#1E1A18" stroke-width="1.2"/>
  <g class="pups"><circle cx="53" cy="67" r="3.3" fill="#111"/><circle cx="73" cy="67" r="3.3" fill="#111"/></g>
  <circle cx="42" cy="80" r="3.4" fill="#FF7A8A" opacity=".75"/><circle cx="79" cy="79" r="3.4" fill="#FF7A8A" opacity=".75"/>
  <path d="M50 81h20c-1 6-5 8-10 8s-9-2-10-8z" fill="#7A1F1F" stroke="#1E1A18" stroke-width="1.2" stroke-linejoin="round"/>
  <path d="M51 81.2h18v2.4H51z" fill="#fff"/>
  <path d="M63 85c1 5 4 8 7 7s3-5 1-8z" fill="#FF7A8A" stroke="#1E1A18" stroke-width="1"/>
  ${RIBBON('СТАРТ', '#B0262B', '#E0393E', '#fff')}`;
/* «Первый рекорд»: гиря в короне набекрень, зажмурилась от счастья, кулак вверх, вокруг искры */
const prb = (id: string) => `${HEX(id)}
  ${ARM_L}
  <g class="arm"><path d="M85 79c9-5 11-14 8-24" fill="none" stroke="#1E1A18" stroke-width="6" stroke-linecap="round"/><circle cx="93" cy="52" r="5.5" fill="#1E1A18"/></g>
  ${KB(id)}
  <g transform="rotate(-10 60 52)"><path d="M45 59l-2-14 8.5 6.5L60 41l8.5 10.5L77 45l-2 14z" fill="#FFC531" stroke="#1E1A18" stroke-width="1.6" stroke-linejoin="round"/>
    <path d="M45.6 55h28.8" stroke="#1E1A18" stroke-width="1.2"/><circle cx="60" cy="50" r="2.2" fill="#FF3B30" stroke="#1E1A18" stroke-width=".6"/></g>
  <path d="M44 72q6-7 12 0M64 71q6-7 12 0" fill="none" stroke="#fff" stroke-width="3.4" stroke-linecap="round"/>
  <circle cx="43" cy="80" r="3.5" fill="#FF7A8A" opacity=".75"/><circle cx="78" cy="79" r="3.5" fill="#FF7A8A" opacity=".75"/>
  <path d="M48 79c4 13 20 13 24 0z" fill="#7A1F1F" stroke="#1E1A18" stroke-width="1.2" stroke-linejoin="round"/>
  <path d="M50 79.3h20l-1 3H51z" fill="#fff"/><ellipse cx="60" cy="86.5" rx="5" ry="2.6" fill="#FF7A8A"/>
  <path class="spark" d="M30 34l1.6 4.4 4.4 1.6-4.4 1.6L30 46l-1.6-4.4-4.4-1.6 4.4-1.6z" fill="#fff"/>
  <path class="spark" d="M84 28l1 2.8 2.8 1-2.8 1-1 2.8-1-2.8-2.8-1 2.8-1z" fill="#fff"/>
  ${RIBBON('РЕКОРД', '#D99A00', '#F5B91E', '#1E1A18')}`;
/* «Первый замер»: гиря обмотана рулеткой по поясу, косится на деления, держит кончик ленты и потеет */
const MSB_TICKS = Array.from({ length: 15 }, (_, i) => {
  const x = 38 + i * 3,
    t = (x - 33.5) / 53;
  return `M${x} ${(84.8 + 24 * t * (1 - t)).toFixed(1)}v${i % 2 ? 2.2 : 3.6}`;
}).join('');
const msb = (id: string) => `${HEX(id)}
  ${SWEAT}
  ${ARM_L}
  <path d="M84 70c8 3 12 10 12 19" fill="none" stroke="#1E1A18" stroke-width="6" stroke-linecap="round"/>
  ${KB(id)}
  <circle cx="50" cy="66" r="7.5" fill="#fff" stroke="#1E1A18" stroke-width="1.2"/>
  <circle cx="70" cy="66" r="7.5" fill="#fff" stroke="#1E1A18" stroke-width="1.2"/>
  <g class="pups"><circle cx="51.5" cy="69.5" r="3.3" fill="#111"/><circle cx="71.5" cy="69.5" r="3.3" fill="#111"/></g>
  <circle cx="42" cy="77" r="3.2" fill="#FF7A8A" opacity=".75"/><circle cx="78" cy="77" r="3.2" fill="#FF7A8A" opacity=".75"/>
  <ellipse cx="60" cy="78.5" rx="3.2" ry="3.8" fill="#7A1F1F" stroke="#1E1A18" stroke-width="1.2"/>
  <path d="M33.5 84Q60 96 86.5 84L84.5 91Q60 101 35.5 91z" fill="#FFD23F" stroke="#1E1A18" stroke-width="1.2" stroke-linejoin="round"/>
  <path d="${MSB_TICKS}" stroke="#1E1A18" stroke-width=".9"/>
  <path d="M86 84.5l11 5.5-2 5-10.5-4.5z" fill="#FFD23F" stroke="#1E1A18" stroke-width="1.2" stroke-linejoin="round"/>
  <circle cx="96.5" cy="91.5" r="4.6" fill="#1E1A18"/>
  ${RIBBON('ЗАМЕР', '#2F64C8', '#3D7BE0', '#fff')}`;

export type AchId = 'start' | 'wk' | 'pr' | 'meas';
interface AchDef {
  name: string;
  art: (id: string) => string;
  tip: string;
  tx: string;
  card: [string, string];
  did: (she: boolean) => string;
  share: (she: boolean) => string;
  file: string;
}
export const ACH: Record<AchId, AchDef> = {
  start: {
    name: ACH_NAME,
    art: kmb,
    tip: 'Пройдите первые шаги на главной',
    tx: 'Все первые шаги пройдены — вы в строю! Значок уже в профиле, а тренер увидит это в своей ленте.',
    card: ['КУРС МОЛОДОГО', 'БОЙЦА'],
    did: (she) => (she ? 'прошла' : 'прошёл') + ' первые шаги',
    share: (she) => `${she ? 'Прошла' : 'Прошёл'} «Курс молодого бойца» в Тренерграм 💪`,
    file: 'kmb',
  },
  wk: {
    name: 'Первая тренировка',
    art: wkb,
    tip: 'Завершите тренировку — кнопка внизу экрана тренировки',
    tx: 'Начало положено! Значок уже в профиле, а тренер увидит это в своей ленте.',
    card: ['ПЕРВАЯ', 'ТРЕНИРОВКА'],
    did: (she) => (she ? 'завершила' : 'завершил') + ' первую тренировку',
    share: () => 'Моя первая тренировка в Тренерграм 💪',
    file: 'trenirovka',
  },
  pr: {
    name: 'Первый рекорд',
    art: prb,
    tip: 'Внесите рекорд в профиле или прямо в тренировке',
    tx: 'Веса в тренировках теперь считаются от вашего рекорда. Значок уже в профиле, а тренер увидит это в своей ленте.',
    card: ['ПЕРВЫЙ', 'РЕКОРД'],
    did: (she) => (she ? 'внесла' : 'внёс') + ' первый рекорд',
    share: () => 'Мой первый рекорд в Тренерграм 💪',
    file: 'rekord',
  },
  meas: {
    name: 'Первый замер',
    art: msb,
    tip: 'Добавьте замер во вкладке «Замеры»',
    tx: 'Первая точка на графике — со следующим замером появится динамика. Значок уже в профиле, а тренер увидит это в своей ленте.',
    card: ['ПЕРВЫЙ', 'ЗАМЕР'],
    did: (she) => (she ? 'сделала' : 'сделал') + ' первый замер',
    share: () => 'Мой первый замер в Тренерграм 💪',
    file: 'zamer',
  },
};
const badgeHtml = (id: AchId, cls: string, lock?: boolean) =>
  `<svg class="${cls}" viewBox="0 0 120 120" aria-hidden="true">${
    lock
      ? `${HEX_OFF}
     <g fill="none" stroke="var(--tx4)" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"><rect x="44" y="56" width="32" height="24" rx="4"/><path d="M50 56v-7a10 10 0 0 1 20 0v7"/></g>`
      : ACH[id].art(cls)
  }</svg>`;
const Badge = ({ id, cls, lock }: { id: AchId; cls: string; lock?: boolean }) => <span style={{ display: 'contents' }} dangerouslySetInnerHTML={{ __html: badgeHtml(id, cls, lock) }} />;

/** Кто прошёл: для открытки и подписи */
export interface AchWho {
  first: string;
  she: boolean;
  trainer: string | null;
}

/** Открытка 1080×1350 (формат поста и сторис): значок, название (в две строки), кто и у какого тренера */
interface Card {
  kicker: string;
  card: [string, string];
  art: (id: string) => string;
  did: (she: boolean) => string;
  file: string;
}
function renderCard(c: Card, w: AchWho): Promise<{ blob: Blob; url: string }> {
  return new Promise((res, rej) => {
    const W = 1080,
      H = 1350,
      cv = document.createElement('canvas');
    cv.width = W;
    cv.height = H;
    const g = cv.getContext('2d')!;
    const bg = g.createRadialGradient(W / 2, 560, 60, W / 2, 560, 900);
    bg.addColorStop(0, '#3A2418');
    bg.addColorStop(1, '#141110');
    g.fillStyle = bg;
    g.fillRect(0, 0, W, H);
    const img = new Image();
    img.onerror = rej;
    img.onload = () => {
      g.save();
      g.shadowColor = 'rgba(252,82,0,.55)';
      g.shadowBlur = 90;
      g.drawImage(img, W / 2 - 330, 170, 660, 660);
      g.restore();
      g.textAlign = 'center';
      g.fillStyle = '#FF9A5A';
      g.font = '800 40px Montserrat, Inter, sans-serif';
      g.fillText(c.kicker, W / 2, 108);
      g.fillStyle = '#fff';
      g.font = '900 84px Montserrat, Inter, sans-serif';
      g.fillText(c.card[0], W / 2, 935);
      g.fillText(c.card[1], W / 2, 1025);
      g.fillStyle = 'rgba(255,255,255,.75)';
      g.font = '500 40px Inter, sans-serif';
      g.fillText(`${w.first || 'Я'} ${c.did(w.she)} в Тренерграм`, W / 2, 1105);
      if (w.trainer) g.fillText('Тренер — ' + w.trainer, W / 2, 1160);
      g.fillStyle = '#FC5200';
      g.fillRect(W / 2 - 60, 1218, 120, 6);
      g.fillStyle = '#fff';
      g.font = '900 44px Montserrat, Inter, sans-serif';
      g.fillText('ТРЕНЕРГРАМ', W / 2, 1290);
      cv.toBlob((b) => (b ? res({ blob: b, url: URL.createObjectURL(b) }) : rej(new Error('canvas'))), 'image/jpeg', 0.92);
    };
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120">${c.art('card')}</svg>`);
  });
}

/** Поделиться открыткой: системный шаринг файла (Web Share), в Capacitor — Share-плагин через
    временный файл, если Web Share с файлами недоступен; иначе — лист с открыткой на сохранение. */
async function shareFile(blob: Blob, fileName: string, text: string, onSheet: (s: { url: string; text: string }) => void) {
  const url = URL.createObjectURL(blob);
  const file = new File([blob], fileName, { type: 'image/jpeg' });
  try {
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], text });
      return;
    }
  } catch (e) {
    if ((e as Error).name === 'AbortError') return;
  }
  if (isNative()) {
    try {
      const [{ Filesystem, Directory }, { Share }] = await Promise.all([import('@capacitor/filesystem'), import('@capacitor/share')]);
      const b64 = await new Promise<string>((res, rej) => {
        const r = new FileReader();
        r.onload = () => res((r.result as string).split(',')[1] || '');
        r.onerror = rej;
        r.readAsDataURL(blob);
      });
      const written = await Filesystem.writeFile({ path: fileName, data: b64, directory: Directory.Cache });
      await Share.share({ url: written.uri, text });
      return;
    } catch {
      /* плагин недоступен/отменено — открытка листом ниже */
    }
  }
  /* Картинкой поделиться нельзя (компьютер, старый браузер) — показываем открытку, её можно сохранить */
  onSheet({ url, text });
}

/** Финал: затемнение, конфетти, значок влетает с поворотом, гиря косит глазами и машет рукой;
    «Поделиться» — главная кнопка, «Отлично» — закрыть */
export function AchFinal({ id, open, onClose, who }: { id: AchId; open: boolean; onClose: () => void; who: AchWho }) {
  const a = ACH[id];
  const { mounted, out } = useLeave(open, 260);
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState<{ url: string; text: string } | null>(null);
  useEffect(() => {
    if (!open) return;
    try {
      navigator.vibrate?.([30, 60, 40]);
    } catch {
      /* без вибрации */
    }
  }, [open]);
  if (!mounted) return null;
  const share = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { blob } = await renderCard({ kicker: 'ДОСТИЖЕНИЕ', card: a.card, art: a.art, did: a.did, file: a.file }, who);
      await shareFile(blob, a.file + '-trenergram.jpg', a.share(who.she), setSheet);
    } catch {
      toast('Открытку не удалось подготовить');
    } finally {
      setBusy(false);
    }
  };
  const cols = ['#FC5200', '#FF7A1F', '#FFB27A', '#8E9F49', '#fff'];
  return createPortal(
    <>
      <div className={'achw' + (out ? ' out' : '')}>
        <div className="achbg" />
        <div className="confetti" aria-hidden="true">
          {Array.from({ length: 36 }, (_, i) => (
            <i
              key={i}
              style={
                {
                  left: ((i * 37) % 100) + '%',
                  background: cols[i % 5],
                  animationDelay: ((i % 9) * 60 + 150) + 'ms',
                  '--r': ((i * 53) % 360) + 'deg',
                  '--x': (((i * 29) % 60) - 30) + 'px',
                  '--d': (1500 + ((i * 71) % 900)) + 'ms',
                } as CSSProperties
              }
            />
          ))}
        </div>
        <div className="achc" role="dialog" aria-label={`Достижение «${a.name}»`}>
          <div className="achb">
            <Badge id={id} cls="achsvg" />
            <span className="shine" />
          </div>
          <em>Достижение</em>
          <h2>{a.name}</h2>
          <p>{a.tx}</p>
          <button className="btn pri" onClick={share} disabled={busy}>
            <I n="share" />
            {busy ? 'Готовим открытку…' : 'Поделиться'}
          </button>
          <button className="lnk achok" onClick={onClose}>
            Отлично
          </button>
        </div>
      </div>
      {sheet ? (
        <div className="shw achsh">
          <div className="shbg" onClick={() => setSheet(null)} />
          <div className="sheet" role="dialog" aria-label="Поделиться">
            <i className="grab" />
            <div className="shp">
              <img className="achcard" src={sheet.url} alt={`Открытка «${a.name}»`} />
              <span>
                <b>{sheet.text}</b>
                <s>Открытка · JPG</s>
              </span>
            </div>
            <a className="btn pri" href={sheet.url} download={a.file + '-trenergram.jpg'} onClick={() => setTimeout(() => setSheet(null), 300)}>
              Сохранить открытку
            </a>
            <button className="lnk" onClick={() => setSheet(null)}>
              Отмена
            </button>
          </div>
        </div>
      ) : null}
    </>,
    document.getElementById('dev') || document.body,
  );
}

/* ═══ Огонёк серии ═══ (ACH-2)
   Пламя с лицом. На 1–2 тренировках — простое; с каждым рубежом (3/7/14/30/50/100)
   крупнее, качается сильнее и обрастает снаряжением: руки с бицепсами, повязка,
   тёмные очки, золотая цепь, плащ, корона. Пока серии нет — серый и спит. */
const fireT = (n: number) => STREAK_MS.filter((m) => n >= m).length;
const FIRE_K = [0.84, 0.9, 0.94, 0.98, 1.02, 1.06, 1.1];
const FIRE_PATH = 'M60 12c6 16 30 26 30 54 0 19-13 34-30 34S30 85 30 66c0-14 7-24 16-30-1 9 2 15 8 18-3-15 0-29 6-42z';
const FIRE_ARM = `<path d="M36 80H14q-6 0-6-6V54q0-10 8-10 6 1 5 8l-1 10q6-8 13-2 4 4 3 8z" fill="#FF8A2A" stroke="#1E1A18" stroke-width="1.8" stroke-linejoin="round"/>
  <path d="M24 60q4-3 8 0" fill="none" stroke="#FFD9A0" stroke-width="1.6" stroke-linecap="round"/>
  <circle cx="14.5" cy="48" r="6" fill="#FF8A2A" stroke="#1E1A18" stroke-width="1.8"/>`;
export function fireSvg(id: string, n: number, k: number): string {
  const t = fireT(n);
  if (!n)
    return `<g transform="translate(60 100) scale(${k}) translate(-60 -100)">
    <path d="${FIRE_PATH}" fill="var(--line2)" stroke="var(--tx4)" stroke-width="2" stroke-linejoin="round"/>
    <path d="M50 74q3 3 6 0M64 74q3 3 6 0M56 83h8" fill="none" stroke="var(--tx4)" stroke-width="2.2" stroke-linecap="round"/></g>`;
  const arm = `<g class="acc armw"><g transform="translate(36 78) scale(${t >= 2 ? 1 : 0.78}) translate(-36 -78)"><g class="flex">${FIRE_ARM}</g></g></g>`;
  return `<g transform="translate(60 100) scale(${k}) translate(-60 -100)"><g class="sway">
    <defs><linearGradient id="fo${id}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#FFB23F"/><stop offset=".55" stop-color="#FF6A1A"/><stop offset="1" stop-color="#E23A00"/></linearGradient>
      <linearGradient id="fc${id}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#FFF4C2"/><stop offset="1" stop-color="#FFD23F"/></linearGradient></defs>
    ${t >= 5 ? `<g class="acc capew"><path class="cape" d="M38 58Q60 50 82 58L98 104Q60 110 22 104z" fill="#C8102E" stroke="#1E1A18" stroke-width="1.6" stroke-linejoin="round"/></g>` : ''}
    ${t >= 1 ? `${arm}<g transform="matrix(-1 0 0 1 120 0)">${arm}</g>` : ''}
    <path class="fo" d="${FIRE_PATH}" fill="url(#fo${id})" stroke="#1E1A18" stroke-width="2" stroke-linejoin="round"/>
    <path class="fc" d="M60 44c4 10 17 17 17 34 0 11-8 19-17 19s-17-8-17-19c0-9 5-15 10-18 0 6 2 9 5 10-1-9 0-17 2-26z" fill="url(#fc${id})"/>
    <circle cx="53" cy="72" r="5.5" fill="#fff" stroke="#1E1A18" stroke-width="1.2"/><circle cx="67" cy="72" r="5.5" fill="#fff" stroke="#1E1A18" stroke-width="1.2"/>
    <g class="pups"><circle cx="54" cy="71" r="2.6" fill="#111"/><circle cx="68" cy="71" r="2.6" fill="#111"/></g>
    <circle cx="47.5" cy="80" r="2.6" fill="#FF7A8A" opacity=".8"/><circle cx="72.5" cy="80" r="2.6" fill="#FF7A8A" opacity=".8"/>
    <path d="M54 80c2 7 10 7 12 0z" fill="#7A1F1F" stroke="#1E1A18" stroke-width="1" stroke-linejoin="round"/>
    ${t >= 2 ? `<g class="acc band"><path d="M30.5 60Q60 52 89.5 59l.5 6Q60 58 31 66z" fill="#E8323C" stroke="#1E1A18" stroke-width="1.4" stroke-linejoin="round"/>
      <path d="M31 63Q60 55 89.7 62" fill="none" stroke="#fff" stroke-width="1.3" opacity=".85"/>
      <g class="tails"><path d="M89 60l12-5 1 5-12 4zM89 63l11 4-2 4-10-5z" fill="#E8323C" stroke="#1E1A18" stroke-width="1.2" stroke-linejoin="round"/></g></g>` : ''}
    ${t >= 3 ? `<g class="acc shades"><path d="M44 67h32" stroke="#1E1A18" stroke-width="2.4" stroke-linecap="round"/>
      <path d="M45 67h14v3q0 6-7 6t-7-6zM61 67h14v3q0 6-7 6t-7-6z" fill="#1E1A18"/>
      <path d="M48 69.5h3M64 69.5h3" stroke="#fff" stroke-width="1.4" stroke-linecap="round" opacity=".8"/></g>` : ''}
    ${t >= 4 ? `<g class="acc chainw"><g class="chain"><path d="M41 82Q60 99 79 82" fill="none" stroke="#B88700" stroke-width="3.4"/>
      <path d="M41 82Q60 99 79 82" fill="none" stroke="#FFD54A" stroke-width="2.2" stroke-dasharray="2.6 1.6"/>
      <path d="M57.5 92.5a2.5 2.5 0 0 1 5 0" fill="none" stroke="#B88700" stroke-width="1.6"/><circle cx="60" cy="95.5" r="3.6" fill="#FFC531" stroke="#1E1A18" stroke-width="1"/></g></g>` : ''}
    ${t >= 6 ? `<g class="acc crown"><g transform="translate(1 10) rotate(14 60 16)"><path d="M49 22l-2-11 7 5 6-9 6 9 7-5-2 11z" fill="#FFC531" stroke="#1E1A18" stroke-width="1.4" stroke-linejoin="round"/>
      <path d="M49.5 19h21" stroke="#1E1A18" stroke-width="1"/><circle cx="60" cy="15" r="1.8" fill="#FF3B30" stroke="#1E1A18" stroke-width=".5"/></g></g>` : ''}
  </g></g>`;
}
/** Значок огонька на шестиграннике (полка, строка серии) */
const StreakBadge = ({ n, cls }: { n: number; cls?: string }) => (
  <span
    style={{ display: 'contents' }}
    dangerouslySetInnerHTML={{ __html: `<svg class="${cls || ''}" viewBox="0 0 120 120" aria-hidden="true">${n ? HEX('stk' + n) : HEX_OFF}${fireSvg('stk' + n, n, 0.8)}</svg>` }}
  />
);
const streakWord = (n: number) => plural3(n, 'тренировка', 'тренировки', 'тренировок');
/** Цитата для статичного показа серии (тап по строке в профиле) — без сервера, без расхода «непоказанных» */
export const streakQuote = (n: number): [string, string] => QUOTES[n % QUOTES.length];

/** Полка в профиле: четыре значка «за первое» + строка серии тренировок */
export function AchShelf({
  ach,
  streak,
  today,
  onOpen,
  onStreak,
}: {
  ach: { start?: string; wk?: string; pr?: string; meas?: string };
  streak: { cur: number; best: number };
  today: string;
  onOpen: (id: AchId) => void;
  onStreak: () => void;
}) {
  const ids: AchId[] = ['start', 'wk', 'pr', 'meas'];
  const got = ids.filter((id) => ach[id]).length;
  const cell = (id: AchId) => {
    const date = ach[id];
    const a = ACH[id];
    return (
      <button key={id} className={'bdg' + (date ? '' : ' lock')} onClick={() => (date ? onOpen(id) : toast(a.tip))}>
        <Badge id={id} cls={'bsvg' + id} lock={!date} />
        <b>{date ? a.name : 'Скоро'}</b>
        <s>{date ? shortDate(date, today) : ''}</s>
      </button>
    );
  };
  const n = streak.cur;
  const nx = n ? streakNext(n) : STREAK_MS[0];
  return (
    <section className="card shelf">
      <div className="shh">
        <b>Мои достижения</b>
        <span>{got} из 4</span>
      </div>
      <div className="bdgs">{ids.map(cell)}</div>
      <button className={'stkrow' + (n ? '' : ' off')} onClick={onStreak}>
        <StreakBadge n={n} />
        <span>
          <b>{n ? `${n} ${streakWord(n)} подряд` : 'Серии ещё нет'}</b>
          <s>{n ? `До следующей ступени — ${nx - n} ${streakWord(nx - n)}` : 'Тренируйтесь без пропусков'}</s>
        </span>
        <I n="right" />
      </button>
    </section>
  );
}

/* ═══ Момент серии ═══ (ACH-2) — вместо тоста после «Завершить тренировку»: тёмный экран, снизу
   летят искры, огонёк вспыхивает, число перекатывается from → to, ниже дорожка до цели со счётчиком
   «1 / 3» и цитата. На рубеже (milestone) — огонёк эволюционирует, дорожка раздвигается до новой цели.
   from === to (тап по строке серии в профиле) — статичный показ без анимаций и искр. */
const Track = ({ n, hi, add, from, just }: { n: number; hi: number; add?: number; from?: number; just?: boolean }) =>
  hi <= 14 ? (
    <div className="stktr">
      {Array.from({ length: hi }, (_, i) => {
        const isAdd = add != null && i >= add;
        return <i key={i} className={[i < n && 'on', just && i === n - 1 && 'just', isAdd && 'add'].filter(Boolean).join(' ')} style={isAdd ? ({ animationDelay: (i - add) * 60 + 'ms' } as CSSProperties) : undefined} />;
      })}
    </div>
  ) : (
    <div className="stkbar">
      <i className={from != null ? 'shrink' : ''} style={{ width: (n / hi) * 100 + '%', ...(from != null ? ({ '--from': from + '%' } as CSSProperties) : {}) }} />
    </div>
  );

export function StreakShow({
  open,
  onClose,
  from,
  to,
  milestone,
  record,
  quote,
  who,
}: {
  open: boolean;
  onClose: () => void;
  from: number;
  to: number;
  milestone: boolean;
  record: boolean;
  quote: [string, string];
  who: AchWho;
}) {
  const { mounted, out } = useLeave(open, 260);
  const up = to > from;
  const t = fireT(to);
  const t0 = fireT(from);
  const evo = milestone && t > t0 && !matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hi = milestone ? to : streakNext(to);
  const nx = streakNext(to);
  const fest = milestone || record;
  const [track, setTrack] = useState<{ hi: number; add?: number; from?: number }>({ hi });
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState<{ url: string; text: string } | null>(null);
  useEffect(() => {
    if (!open) return;
    setTrack({ hi });
    try {
      navigator.vibrate?.(record && !evo ? [30, 60, 40] : milestone ? 30 : 25);
    } catch {
      /* без вибрации */
    }
    if (!milestone) return;
    const at = evo ? 2900 : 1700;
    const tm = setTimeout(() => setTrack(hi <= 14 && nx <= 14 ? { hi: nx, add: hi } : { hi: nx, from: 100 }), at);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!mounted) return null;
  const card: Card = { kicker: record ? 'НОВЫЙ РЕКОРД СЕРИИ' : 'СЕРИЯ', card: [`${to} ${streakWord(to).toUpperCase()}`, 'ПОДРЯД'], art: (id) => `${HEX(id)}${fireSvg(id, to, 0.8)}`, did: () => 'тренируется без пропусков', file: 'seriya' };
  const share = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { blob } = await renderCard(card, who);
      await shareFile(blob, 'seriya-trenergram.jpg', `${to} ${streakWord(to)} подряд в Тренерграм 🔥`, setSheet);
    } catch {
      toast('Открытку не удалось подготовить');
    } finally {
      setBusy(false);
    }
  };
  const cols = ['#FC5200', '#FF7A1F', '#FFB27A', '#FFD23F', '#fff'];
  const embers = up ? Array.from({ length: 22 + t * 4 }, (_, i) => i) : [];
  return createPortal(
    <>
      <div className={'achw' + (out ? ' out' : '')}>
        <div className="achbg" />
        {up ? (
          <div className="embers" aria-hidden="true">
            {embers.map((i) => (
              <i key={i} style={{ left: ((i * 41) % 100) + '%', '--s': 3 + (i % 4) + 'px', '--x': ((i * 23) % 50) - 25 + 'px', '--d': 1800 + ((i * 97) % 1400) + 'ms', '--dl': (i * 173) % 1600 + 'ms' } as CSSProperties} />
            ))}
          </div>
        ) : null}
        {up && fest ? (
          <div className="confetti" aria-hidden="true">
            {Array.from({ length: 36 }, (_, i) => (
              <i key={i} style={{ left: ((i * 37) % 100) + '%', background: cols[i % 5], animationDelay: (i % 9) * 60 + (evo ? 2400 : 900) + 'ms', '--r': ((i * 53) % 360) + 'deg', '--x': ((i * 29) % 60) - 30 + 'px', '--d': 1500 + ((i * 71) % 900) + 'ms' } as CSSProperties} />
            ))}
          </div>
        ) : null}
        <div className={'achc stkc' + (evo ? ' evo' : '')} role="dialog" aria-label={`Серия: ${to} ${streakWord(to)} подряд`}>
          <div className={'stkfire' + (up ? ' go' : '')}>
            {evo ? (
              <div className="stkevo">
                <span className={'achsvg ft' + t0 + ' evo-old'} dangerouslySetInnerHTML={{ __html: fireSvg('stko', from, FIRE_K[t0]) }} style={{ display: 'contents' }} />
                <span className={'achsvg ft' + t + ' evo-new'} dangerouslySetInnerHTML={{ __html: fireSvg('stkn', to, FIRE_K[t]) }} style={{ display: 'contents' }} />
                <i className="evo-flash" />
              </div>
            ) : (
              <span className={'achsvg ft' + t} dangerouslySetInnerHTML={{ __html: fireSvg('stk', to, FIRE_K[t]) }} style={{ display: 'contents' }} />
            )}
          </div>
          <div className="stknum">
            {up ? (
              <span className="roll">
                <b>{from}</b>
                <b>{to}</b>
              </span>
            ) : (
              <b>{to}</b>
            )}
          </div>
          <em>{record ? 'Новый рекорд серии!' : streakWord(to) + ' подряд'}</em>
          <div className="stkprog">
            <Track n={to} hi={track.hi} add={track.add} from={track.from} just={up} />
            <span className={track.add != null || track.from != null ? 'upd' : ''}>
              {to} / {track.hi}
            </span>
          </div>
          <blockquote className="stkq">
            «{quote[0]}»<cite>{quote[1]}</cite>
          </blockquote>
          {fest ? (
            <>
              <button className="btn pri" onClick={share} disabled={busy}>
                <I n="share" />
                {busy ? 'Готовим открытку…' : 'Поделиться'}
              </button>
              <button className="lnk achok" onClick={onClose}>
                Продолжить
              </button>
            </>
          ) : (
            <button className="btn pri" onClick={onClose}>
              Продолжить
            </button>
          )}
        </div>
      </div>
      {sheet ? (
        <div className="shw achsh">
          <div className="shbg" onClick={() => setSheet(null)} />
          <div className="sheet" role="dialog" aria-label="Поделиться">
            <i className="grab" />
            <div className="shp">
              <img className="achcard" src={sheet.url} alt="Открытка «Серия»" />
              <span>
                <b>{sheet.text}</b>
                <s>Открытка · JPG</s>
              </span>
            </div>
            <a className="btn pri" href={sheet.url} download="seriya-trenergram.jpg" onClick={() => setTimeout(() => setSheet(null), 300)}>
              Сохранить открытку
            </a>
            <button className="lnk" onClick={() => setSheet(null)}>
              Отмена
            </button>
          </div>
        </div>
      ) : null}
    </>,
    document.getElementById('dev') || document.body,
  );
}

/* ═══ Серия прервалась ═══ (ACH-2) — один раз, при следующем входе после пропуска: огонёк гаснет,
   число откатывается к нулю, цитата о неудачах (отдельный пул) и «Начать заново». Без «Поделиться» —
   неудача не шарится. Серии короче первого рубежа обрываются молча (сервер их сюда не присылает). */
export function StreakLostShow({ open, onClose, n, best, quote }: { open: boolean; onClose: () => void; n: number; best: number; quote: [string, string] }) {
  const { mounted, out } = useLeave(open, 260);
  const t = fireT(n);
  useEffect(() => {
    if (!open) return;
    try {
      navigator.vibrate?.(60);
    } catch {
      /* без вибрации */
    }
  }, [open]);
  if (!mounted) return null;
  return createPortal(
    <div className={'achw' + (out ? ' out' : '')}>
      <div className="achbg" />
      <div className="achc stkc lost" role="dialog" aria-label={`Серия прервалась: было ${n} ${streakWord(n)} подряд`}>
        <div className="stkfire">
          <div className="stkevo">
            <span className={'achsvg ft' + t + ' out-old'} dangerouslySetInnerHTML={{ __html: fireSvg('stkl', n, FIRE_K[t]) }} style={{ display: 'contents' }} />
            <span className="achsvg ft0 out-new" dangerouslySetInnerHTML={{ __html: fireSvg('stkz', 0, FIRE_K[0]) }} style={{ display: 'contents' }} />
            <span className="smoke">
              <i />
              <i />
              <i />
            </span>
          </div>
        </div>
        <div className="stknum">
          <span className="roll">
            <b>{n}</b>
            <b>0</b>
          </span>
        </div>
        <em>
          Серия прервалась<span className="stkbest">рекорд {best}</span>
        </em>
        <blockquote className="stkq">
          «{quote[0]}»<cite>{quote[1]}</cite>
        </blockquote>
        <button className="btn pri" onClick={onClose}>
          Начать заново
        </button>
      </div>
    </div>,
    document.getElementById('dev') || document.body,
  );
}

/** Единая точка показа очереди (ACH-2) — смонтирована один раз в App, рендерит то, что сейчас в очереди
    (achQueue.ts): финал ачивки, момент серии или срыв. Закрытие — dequeue(), дальше сама очередь. */
export function AchQueueHost() {
  const m = useAchQueue();
  const me = useMe();
  if (!m || !me.data) return null;
  const who: AchWho = { first: me.data.first, she: me.data.sex === 'f', trainer: me.data.trainer ? [me.data.trainer.first, me.data.trainer.last].filter(Boolean).join(' ') : null };
  if (m.kind === 'ach') return <AchFinal id={m.id} open onClose={dequeue} who={who} />;
  if (m.kind === 'streak') return <StreakShow open onClose={dequeue} from={m.from} to={m.to} milestone={m.milestone} record={m.record} quote={m.quote} who={who} />;
  return <StreakLostShow open onClose={dequeue} n={m.n} best={m.best} quote={m.quote} />;
}
